'use client';

import { useState, useEffect } from 'react';
import Header from '@/components/common/Header';
import TrustIndicatorBar from '@/components/common/TrustIndicatorBar';
import StickyCartSummary from '@/components/common/StickyCartSummary';
import SectionProgressIndicator from '@/components/common/SectionProgressIndicator';
import QuickActionButton from '@/components/common/QuickActionButton';
import HeroSection from './HeroSection';
import ProblemSection from './ProblemSection';
import ProductGallery from './ProductGallery';
import BenefitsSection from './BenefitsSection';
import TestimonialsSection from './TestimonialsSection';
import HeritageSection from './HeritageSection';
import IngredientsSection from './IngredientsSection';
import RecipesSection from './RecipesSection';
import PricingSection from './PricingSection';
import FAQSection from './FAQSection';
import CTASection from './CTASection';
import FooterSection from './FooterSection';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
}

const LandingPageInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleAddToCart = (productId: string) => {
    if (!isHydrated) return;

    const productMap: Record<string, Omit<CartItem, 'quantity'>> = {
      p1: {
        id: 'p1',
        name: 'Mango Pickle',
        price: 299,
        image: '/assets/images/mango-pickle-1766907651361.jpg',
      },
      p2: {
        id: 'p2',
        name: 'Mixed Vegetable Pickle',
        price: 249,
        image: '/assets/images/Mixed-Vegetable-Pickle-1766908363007.jpg',
      },
      p3: {
        id: 'p3',
        name: 'Lemon Pickle',
        price: 199,
        image: '/assets/images/lemon-pickle-1766909294465.jpg',
      },
      p4: {
        id: 'p4',
        name: 'Pure Deshi Ghee',
        price: 899,
        image: '/assets/images/deshi-ghee-1766909394511.jpg',
      },
      p5: {
        id: 'p5',
        name: 'Amla Murraba',
        price: 349,
        image: '/assets/images/amla-murraba-1766909831703.png',
      },
      p6: {
        id: 'p6',
        name: 'Carrot Murraba',
        price: 299,
        image: '/assets/images/carrot-murraba-1766909916911.png',
      },
    };

    const product = productMap[productId];
    if (!product) return;

    setCartItems((prev) => {
      const existingItem = prev.find((item) => item.id === productId);
      if (existingItem) {
        return prev.map((item) =>
          item.id === productId
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const handleSelectPackage = (packageId: string) => {
    if (!isHydrated) return;
    console.log('Selected package:', packageId);
    const element = document.getElementById('products');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleOrderNow = () => {
    if (!isHydrated) return;
    const element = document.getElementById('products');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleViewGallery = () => {
    if (!isHydrated) return;
    const element = document.getElementById('products');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCheckout = () => {
    if (!isHydrated) return;
    console.log('Proceeding to checkout with items:', cartItems);
  };

  const handleViewCart = () => {
    if (!isHydrated) return;
    console.log('Viewing cart with items:', cartItems);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <TrustIndicatorBar />
      <SectionProgressIndicator />
      
      <main>
        <HeroSection onOrderNow={handleOrderNow} onViewGallery={handleViewGallery} />
        <ProblemSection />
        <ProductGallery onAddToCart={handleAddToCart} />
        <BenefitsSection />
        <TestimonialsSection />
        <HeritageSection />
        <IngredientsSection />
        <RecipesSection />
        <PricingSection onSelectPackage={handleSelectPackage} />
        <FAQSection />
        <CTASection onOrderNow={handleOrderNow} />
      </main>

      <FooterSection />

      <StickyCartSummary
        cartItems={cartItems}
        onCheckout={handleCheckout}
        onViewCart={handleViewCart}
      />
      
      <QuickActionButton
        cartItemCount={cartItems.reduce((sum, item) => sum + item.quantity, 0)}
        onOrderNow={handleOrderNow}
        onViewCart={handleViewCart}
      />
    </div>
  );
};

export default LandingPageInteractive;